import { CreateEmspUserOcpiTokenBodyModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users/create-emsp-user-ocpi-token-body.model';
import { GetEmspUsersPaginatedModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/emsp-bo/models/inputs/emsp-users/get-emsp-users-paginated.model';
import { useUrlSyncedFilters } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/hooks';
import { CreateEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/create-emsp-user-ocpi-token.model';
import { DeleteEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/delete-emsp-user-ocpi-token.model';
import { LockEmspUserByIdModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/lock-emsp-user.model';
import { UnlockEmspUserByIdModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/unlock-emsp-user.model';
import { UpdateEmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/inputs/emsp-users/update-emsp-user-ocpi-token.model';
import { EmspUserLockStatusModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/emsp-users/emsp-user-lock-status.model';
import { EmspUserOcpiTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/emsp-users/emsp-user-ocpi-token.model';
import { EmspUserModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/back-office-gateway-emsp-lib/src/outputs/emsp-users/emsp-user.model';
import React, {
  Dispatch,
  SetStateAction,
  createContext,
  useContext,
  useMemo,
} from 'react';
import { DeepPartialSkipArrayKey } from 'react-hook-form';

import {
  TABLE_LIMITS_OPTIONS,
  TABLE_LIMIT_OPTION_MIN,
} from '@app/config/app.config';
import {
  EMSP_BO_EMSP_USER_LIST_OPTIONS_LSK,
  EMSP_BO_USER_FILTERS_LSK,
} from '@app/config/localstorage-keys.const';
import { useLocalStorageOptions } from '@shared/hooks/useLocalStorageOptions';

import {
  EMSP_BO_EMSP_USER_LIST_DEFAULT_OPTIONS,
  EMSP_BO_EMSP_USER_LIST_FILTER_NAME_LIST,
  EMSP_BO_EMSP_USER_LIST_SORT_FIELD,
  EMSP_BO_EMSP_USER_LIST_SORT_ORDER,
  EmspBoEmspUserListOptionsModel,
} from './emsp-bo-emsp-user-list/emsp-bo-emsp-user-list.config';

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoEmspUserContext = createContext<EmspUserModel | null>(null);

export const useEmspBoEmspUserContext = (): EmspUserModel => {
  const context = useContext(EmspBoEmspUserContext);

  if (!context) {
    throw new Error(
      'useEmspBoEmspUserContext must be used within EmspBoEmspUserContext.Provider',
    );
  }

  return context;
};

interface EmspBoEmspUserContextProviderProps {
  data: EmspUserModel;
  children: React.ReactNode;
}

export const EmspBoEmspUserContextProvider = ({
  data,
  children,
}: EmspBoEmspUserContextProviderProps): React.JSX.Element => (
  <EmspBoEmspUserContext.Provider value={data}>
    {children}
  </EmspBoEmspUserContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const LockEmspBoEmspUserContext =
  createContext<LockEmspUserByIdModel | null>(null);

export const useLockEmspBoEmspUserContext = (): LockEmspUserByIdModel => {
  const context = useContext(LockEmspBoEmspUserContext);

  if (!context) {
    throw new Error(
      'useLockEmspBoEmspUserContext must be used within LockEmspBoEmspUserContext.Provider',
    );
  }

  return context;
};

interface LockEmspBoEmspUserContextProviderProps {
  data: LockEmspUserByIdModel;
  children: React.ReactNode;
}

export const LockEmspBoEmspUserContextProvider = ({
  data,
  children,
}: LockEmspBoEmspUserContextProviderProps): React.JSX.Element => (
  <LockEmspBoEmspUserContext.Provider value={data}>
    {children}
  </LockEmspBoEmspUserContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const UnlockEmspBoEmspUserContext =
  createContext<UnlockEmspUserByIdModel | null>(null);

export const useUnlockEmspBoEmspUserContext = (): LockEmspUserByIdModel => {
  const context = useContext(UnlockEmspBoEmspUserContext);

  if (!context) {
    throw new Error(
      'useUnlockEmspBoEmspUserContext must be used within UnlockEmspBoEmspUserContext.Provider',
    );
  }

  return context;
};

interface UnlockEmspBoEmspUserContextProviderProps {
  data: LockEmspUserByIdModel;
  children: React.ReactNode;
}

export const UnlockEmspBoEmspUserContextProvider = ({
  data,
  children,
}: UnlockEmspBoEmspUserContextProviderProps): React.JSX.Element => (
  <UnlockEmspBoEmspUserContext.Provider value={data}>
    {children}
  </UnlockEmspBoEmspUserContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoEmspUserLockStatusContext =
  createContext<EmspUserLockStatusModel | null>(null);

export const useEmspBoEmspUserLockStatusContext =
  (): EmspUserLockStatusModel | null =>
    useContext(EmspBoEmspUserLockStatusContext);

interface EmspBoEmspUserLockStatusContextProviderProps {
  data: EmspUserLockStatusModel | null;
  children: React.ReactNode;
}

export const EmspBoEmspUserLockStatusContextProvider = ({
  data,
  children,
}: EmspBoEmspUserLockStatusContextProviderProps): React.JSX.Element => (
  <EmspBoEmspUserLockStatusContext.Provider value={data}>
    {children}
  </EmspBoEmspUserLockStatusContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export interface EmspBoEmspUserListContextProps {
  getPaginatedQueryParameters: GetEmspUsersPaginatedModel;
  setGetPaginatedQueryParameters: Dispatch<
    SetStateAction<GetEmspUsersPaginatedModel>
  >;
  getPaginatedLimitList: number[];
  columnOptions: EmspBoEmspUserListOptionsModel;
  setColumnOptions: Dispatch<SetStateAction<EmspBoEmspUserListOptionsModel>>;
}

export const EmspBoEmspUserListContext =
  createContext<EmspBoEmspUserListContextProps | null>(null);

export const useEmspBoEmspUserListContext =
  (): EmspBoEmspUserListContextProps => {
    const context = useContext(EmspBoEmspUserListContext);

    if (!context) {
      throw new Error(
        'useEmspBoEmspUserListContext must be used within EmspBoEmspUserListContext.Provider',
      );
    }

    return context;
  };

interface EmspBoEmspUserListContextProviderProps {
  children: React.ReactNode;
}

export const EmspBoEmspUserListContextProvider = ({
  children,
}: EmspBoEmspUserListContextProviderProps): React.JSX.Element => {
  const [getPaginatedQueryParameters, setGetPaginatedQueryParameters] =
    useUrlSyncedFilters<GetEmspUsersPaginatedModel>(
      {
        // Pagination
        limit: TABLE_LIMITS_OPTIONS.at(-1) ?? TABLE_LIMIT_OPTION_MIN,
        offset: 0,
        // Filterable fields
        billingFrequency: [],
        textToSearch: '',
        // Sortable fields
        sortField: EMSP_BO_EMSP_USER_LIST_SORT_FIELD,
        sortOrder: EMSP_BO_EMSP_USER_LIST_SORT_ORDER,
      },
      [
        'limit',
        'offset',
        'sortField',
        'sortOrder',
        'textToSearch',
        ...EMSP_BO_EMSP_USER_LIST_FILTER_NAME_LIST,
      ],
      EMSP_BO_USER_FILTERS_LSK,
    );

  const [columnOptions, setColumnOptions] =
    useLocalStorageOptions<EmspBoEmspUserListOptionsModel>(
      EMSP_BO_EMSP_USER_LIST_DEFAULT_OPTIONS,
      EMSP_BO_EMSP_USER_LIST_OPTIONS_LSK,
    );

  const value = useMemo(
    () => ({
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      getPaginatedLimitList: TABLE_LIMITS_OPTIONS,
      columnOptions,
      setColumnOptions,
    }),
    [
      getPaginatedQueryParameters,
      setGetPaginatedQueryParameters,
      columnOptions,
      setColumnOptions,
    ],
  );

  return (
    <EmspBoEmspUserListContext.Provider value={value}>
      {children}
    </EmspBoEmspUserListContext.Provider>
  );
};

// /////////////////////////////////////////////////////////////////////////////

export const FilterEmspBoEmspUserListContext =
  createContext<DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel> | null>(
    null,
  );

export const useFilterEmspBoEmspUserListContext =
  (): DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel> => {
    const context = useContext(FilterEmspBoEmspUserListContext);

    if (!context) {
      throw new Error(
        'useFilterEmspBoEmspUserListContext must be used within FilterEmspBoEmspUserListContext.Provider',
      );
    }

    return context;
  };

interface FilterEmspBoEmspUserListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterEmspBoEmspUserListContextProvider = ({
  data,
  children,
}: FilterEmspBoEmspUserListContextProviderProps): React.JSX.Element => (
  <FilterEmspBoEmspUserListContext.Provider value={data}>
    {children}
  </FilterEmspBoEmspUserListContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const FilterSearchEmspBoEmspUserListContext =
  createContext<DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel> | null>(
    null,
  );

export const useFilterSearchEmspBoEmspUserListContext =
  (): DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel> => {
    const context = useContext(FilterSearchEmspBoEmspUserListContext);

    if (!context) {
      throw new Error(
        'useFilterSearchEmspBoEmspUserListContext must be used within FilterSearchEmspBoEmspUserListContext.Provider',
      );
    }

    return context;
  };

interface FilterSearchEmspBoEmspUserListContextProviderProps {
  data: DeepPartialSkipArrayKey<GetEmspUsersPaginatedModel>;
  children: React.ReactNode;
}

export const FilterSearchEmspBoEmspUserListContextProvider = ({
  data,
  children,
}: FilterSearchEmspBoEmspUserListContextProviderProps): React.JSX.Element => (
  <FilterSearchEmspBoEmspUserListContext.Provider value={data}>
    {children}
  </FilterSearchEmspBoEmspUserListContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const EmspBoEmspUserOcpiTokenContext =
  createContext<EmspUserOcpiTokenModel | null>(null);

export const useEmspBoEmspUserOcpiTokenContext = (): EmspUserOcpiTokenModel => {
  const context = useContext(EmspBoEmspUserOcpiTokenContext);

  if (!context) {
    throw new Error(
      'useEmspBoEmspUserOcpiTokenContext must be used within EmspBoEmspUserOcpiTokenContext.Provider',
    );
  }

  return context;
};

interface EmspBoEmspUserOcpiTokenContextProviderProps {
  data: EmspUserOcpiTokenModel;
  children: React.ReactNode;
}

export const EmspBoEmspUserOcpiTokenContextProvider = ({
  data,
  children,
}: EmspBoEmspUserOcpiTokenContextProviderProps): React.JSX.Element => (
  <EmspBoEmspUserOcpiTokenContext.Provider value={data}>
    {children}
  </EmspBoEmspUserOcpiTokenContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const CreateEmspBoEmspUserOcpiTokenContext =
  createContext<DeepPartialSkipArrayKey<CreateEmspUserOcpiTokenModel> | null>(
    null,
  );

export const useCreateEmspBoEmspUserOcpiTokenContext =
  (): DeepPartialSkipArrayKey<CreateEmspUserOcpiTokenModel> => {
    const context = useContext(CreateEmspBoEmspUserOcpiTokenContext);

    if (!context) {
      throw new Error(
        'useCreateEmspBoEmspUserOcpiTokenContext must be used within CreateEmspBoEmspUserOcpiTokenContext.Provider',
      );
    }

    return context;
  };

interface CreateEmspBoEmspUserOcpiTokenContextProviderProps {
  data: DeepPartialSkipArrayKey<CreateEmspUserOcpiTokenBodyModel>;
  children: React.ReactNode;
}

export const CreateEmspBoEmspUserOcpiTokenContextProvider = ({
  data,
  children,
}: CreateEmspBoEmspUserOcpiTokenContextProviderProps): React.JSX.Element => (
  <CreateEmspBoEmspUserOcpiTokenContext.Provider value={data}>
    {children}
  </CreateEmspBoEmspUserOcpiTokenContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const DeleteEmspBoEmspUserOcpiTokenContext =
  createContext<DeepPartialSkipArrayKey<DeleteEmspUserOcpiTokenModel> | null>(
    null,
  );

export const useDeleteEmspBoEmspUserOcpiTokenContext =
  (): DeepPartialSkipArrayKey<DeleteEmspUserOcpiTokenModel> => {
    const context = useContext(DeleteEmspBoEmspUserOcpiTokenContext);

    if (!context) {
      throw new Error(
        'useDeleteEmspBoEmspUserOcpiTokenContext must be used within DeleteEmspBoEmspUserOcpiTokenContext.Provider',
      );
    }

    return context;
  };

interface DeleteEmspBoEmspUserOcpiTokenContextProviderProps {
  data: DeleteEmspUserOcpiTokenModel;
  children: React.ReactNode;
}

export const DeleteEmspBoEmspUserOcpiTokenContextProvider = ({
  data,
  children,
}: DeleteEmspBoEmspUserOcpiTokenContextProviderProps): React.JSX.Element => (
  <DeleteEmspBoEmspUserOcpiTokenContext.Provider value={data}>
    {children}
  </DeleteEmspBoEmspUserOcpiTokenContext.Provider>
);

// /////////////////////////////////////////////////////////////////////////////

export const UpdateEmspBoEmspUserOcpiTokenContext =
  createContext<DeepPartialSkipArrayKey<UpdateEmspUserOcpiTokenModel> | null>(
    null,
  );

export const useUpdateEmspBoEmspUserOcpiTokenContext =
  (): DeepPartialSkipArrayKey<UpdateEmspUserOcpiTokenModel> => {
    const context = useContext(UpdateEmspBoEmspUserOcpiTokenContext);

    if (!context) {
      throw new Error(
        'useUpdateEmspBoEmspUserOcpiTokenContext must be used within UpdateEmspBoEmspUserOcpiTokenContext.Provider',
      );
    }

    return context;
  };

interface UpdateEmspBoEmspUserOcpiTokenContextProviderProps {
  data: DeepPartialSkipArrayKey<UpdateEmspUserOcpiTokenModel>;
  children: React.ReactNode;
}

export const UpdateEmspBoEmspUserOcpiTokenContextProvider = ({
  data,
  children,
}: UpdateEmspBoEmspUserOcpiTokenContextProviderProps): React.JSX.Element => (
  <UpdateEmspBoEmspUserOcpiTokenContext.Provider value={data}>
    {children}
  </UpdateEmspBoEmspUserOcpiTokenContext.Provider>
);
